import React, { useState, useEffect } from "react";
import Modal from "src/components/common/Modal";
import Button from "../../Button";
import Input from "../../Input";
import Select from "../../Select";
import { saveReminder, updateReminder } from "src/helpers/saveReminder";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import reminders from "src/constants/reminders";
import { toast } from "sonner";
import {
  ReminderData,
  formatReminderForForm,
} from "src/helpers/reminderHelpers";
import moment from "moment";
import * as reminderActions from "src/redux/slices/reminder";

interface SetFatigueReminderModalProps {
  show: boolean;
  handleClose: () => void;
  existingReminder?: ReminderData | null;
}

const SetFatigueReminderModal: React.FC<SetFatigueReminderModalProps> = ({
  show,
  handleClose,
  existingReminder,
}) => {
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    reminder_time: "",
    frequency: reminders.FREQUENCY_OPTIONS[0].value,
    start_date: "",
    end_date: "",
    selectDays: [] as any,
  });

  // Pre-fill form with existing reminder data
  useEffect(() => {
    if (existingReminder && show) {
      const formattedData = formatReminderForForm(existingReminder);
      setFormData({
        reminder_time: formattedData.customFreqAttributes.defaultTimes[0] || "",
        frequency: formattedData.frequency,
        start_date: moment(formattedData.startTime).format("YYYY-MM-DD"),
        end_date: moment(formattedData.endTime).format("YYYY-MM-DD"),
        selectDays: formattedData.customFreqAttributes.days || [],
      });
    } else if (!existingReminder && show) {
      // Reset form when creating new reminder
      setFormData({
        reminder_time: "",
        frequency: reminders.FREQUENCY_OPTIONS[0].value,
        start_date: "",
        end_date: "",
        selectDays: [] as any,
      });
    }
  }, [existingReminder, show]);

  const refetchReminders = () => {
    if (patient?.data?.patient_id) {
      new Promise((resolve, reject) => {
        dispatch(
          reminderActions.getReminderRequest({
            resolve,
            reject,
            payload: {
              user_id: patient?.data?.patient_id,
            },
          })
        );
      }).catch((error) => {
        console.error("Error refetching reminders:", error);
      });
    }
  };

  const handleSaveReminder = () => {
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      toast.error("Start Date must be before End Date");
      return;
    }

    const payload: any = {
      startTime: formData.start_date,
      endTime: formData.end_date,
      frequency: formData.frequency,
      customFreqAttributes: {
        days: formData.selectDays,
        defaultTimes: [formData.reminder_time],
      },
    };

    // Add type for new reminders, patientReminderId for updates
    if (existingReminder) {
      payload.patientReminderId = existingReminder.patientReminderId;
      updateReminder(payload, dispatch, refetchReminders);
    } else {
      payload.type = "FATIGUE";
      saveReminder(payload, dispatch, refetchReminders);
    }
    handleClose();
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      showCloseButton
      handleClose={handleClose}
      title={existingReminder ? "Update Reminder" : "Set Fatigue Reminder"}
    >
      <div className="relative space-y-4">
        <div className="grid grid-cols-1 gap-y-3">
          <div className="col-span-1">
            <div className="relative w-full">
              <Input
                label="Reminder Time"
                name="reminder_time"
                type="time"
                placeholder="Enter Reminder Time"
                fullWidth
                value={formData.reminder_time}
                step="60"
                onChange={(e) => {
                  setFormData({ ...formData, reminder_time: e.target.value });
                }}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="relative w-full">
              <Select
                label="Frequency"
                name="frequency"
                options={reminders.FREQUENCY_OPTIONS}
                fullWidth
                value={formData.frequency}
                onChange={(e) => {
                  setFormData({ ...formData, frequency: e.target.value });
                }}
              />
            </div>
          </div>
          {formData.frequency === "custom" && (
            <div className="w-full">
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                Custom Days
              </label>
              <div className="flex items-center gap-2 flex-wrap ">
                {reminders.CUSTOM_FREQUENCY_OPTIONS.map((item: any) => {
                  const isSelected = formData.selectDays.includes(
                    item as never
                  );
                  return (
                    <button
                      key={item.day}
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          selectDays: isSelected
                            ? formData.selectDays.filter(
                                (day: any) => day !== item
                              )
                            : [...formData.selectDays, item],
                        })
                      }
                      className={`w-8 h-8 rounded-full text-sm ${
                        isSelected
                          ? "bg-[var(--app-primary)] text-white"
                          : "bg-gray-200 text-[var(--text-secondary)]"
                      }`}
                    >
                      {item.day.charAt(0).toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="col-span-1">
            <div className="relative w-full">
              <Input
                label="Start Date"
                name="start_date"
                type="date"
                placeholder="Enter Start Date"
                fullWidth
                value={formData.start_date}
                onChange={(e) => {
                  setFormData({ ...formData, start_date: e.target.value });
                }}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="relative w-full">
              <Input
                label="End Date (Optional)"
                name="end_date"
                type="date"
                placeholder="Enter End Date"
                fullWidth
                value={formData.end_date}
                onChange={(e) => {
                  setFormData({ ...formData, end_date: e.target.value });
                }}
              />
            </div>
          </div>
          <div className="col-span-1">
            <div className="relative w-full flex items-center justify-between gap-2">
              <div className="w-full">
                <Button
                  variant="outlined"
                  rounded="full"
                  fullWidth
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </div>
              <div className="w-full">
                <Button
                  variant="contained"
                  rounded="full"
                  fullWidth
                  onClick={handleSaveReminder}
                >
                  Save Reminder
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SetFatigueReminderModal;
